<script setup lang="ts">
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusPill,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, ref, useId, watch } from 'vue'
import { api, refusalCode, type TaskDetail, type TaskInputField, type TaskInputValues } from '@/api'

const props = defineProps<{
    task: TaskDetail
    inputFields: TaskInputField[]
    /** A built-in task's one row serves every enterprise, so it takes no values of any one enterprise's own. */
    editable: boolean
}>()
const emit = defineEmits<{ changed: [] }>()

/*
 * An enterprise's own input values (F2): one value per input the task declares, kept on the task, never on a version.
 * Every run takes them, however it starts, under what the start gives and over the version's defaults, so the
 * enterprise running a linked clone fills what its source leaves to each enterprise, as it binds the tools it needs.
 * Every block, chip and control here is the kit's, as on the Triggers panel beside it.
 */
const fieldId = useId()
/** The boolean picker's choice for "no value of our own": a Select item cannot hold an empty string. */
const UNSET = 'unset'

const editing = ref(false)
const saving = ref(false)
const error = ref<string | null>(null)
/** What each field's control holds while editing: text for text, numbers and links, `true`/`false`/unset for a boolean. */
const draft = ref<Record<string, string>>({})

const values = computed<TaskInputValues>(() => props.task.inputValues ?? {})
const missingSetup = computed(() => props.task.configuration?.missingInputs ?? [])
const ignored = computed(() => props.task.ignoredInputValues ?? [])
/** A file is given at each start and kept as that run's file: there is no value of the enterprise's own to keep. */
const settable = computed(() => props.inputFields.filter(field => field.type !== 'file'))

function shown(value: unknown): string {
    return value === undefined || value === null || value === '' ? '' : String(value)
}

/** The enterprise's value for a field, when the current version takes it; ignored ones are listed apart. */
function ownValue(field: TaskInputField): string {
    return ignored.value.includes(field.key) ? '' : shown(values.value[field.key])
}

function startEditing(): void {
    error.value = null
    draft.value = Object.fromEntries(
        settable.value.map(field => {
            const own = ownValue(field)
            return [field.key, field.type === 'boolean' && own === '' ? UNSET : own]
        }),
    )
    editing.value = true
}

/** The values the form describes, or the first field that cannot hold what was typed into it. */
const parsed = computed<{ values: TaskInputValues } | { problem: string }>(() => {
    const out: TaskInputValues = {}
    for (const field of settable.value) {
        const text = (draft.value[field.key] ?? '').trim()
        if (text === '' || text === UNSET) {
            continue
        }
        if (field.type === 'boolean') {
            out[field.key] = text === 'true'
        } else if (field.type === 'number') {
            const number = Number(text)
            if (!Number.isFinite(number)) {
                return { problem: `${field.label} takes a number.` }
            }
            out[field.key] = number
        } else {
            out[field.key] = text
        }
    }
    return { values: out }
})
const problem = computed(() => ('problem' in parsed.value ? parsed.value.problem : null))

/** What a refused save says: the field it names, in words, else the server's own code in brackets. */
function failure(verb: 'save' | 'clear', caught: unknown): string {
    const what = `Could not ${verb} the input values`
    if (!(caught instanceof Error)) {
        return `${what}.`
    }
    const field = (caught as Error & { field?: unknown }).field
    const label = props.inputFields.find(row => row.key === field)?.label ?? field
    if (refusalCode(caught) === 'input_invalid' && typeof label === 'string') {
        return `${what}: ${label} does not take that value.`
    }
    if (refusalCode(caught) === 'input_undeclared' && typeof label === 'string') {
        return `${what}: this task's current version does not declare ${label}.`
    }
    return `${what} (${caught.message}).`
}

async function save(): Promise<void> {
    if (!('values' in parsed.value)) {
        return
    }
    saving.value = true
    error.value = null
    try {
        await api.setTaskInputValues(props.task.id, parsed.value.values)
        editing.value = false
        emit('changed')
    } catch (caught) {
        error.value = failure('save', caught)
    } finally {
        saving.value = false
    }
}

async function clear(): Promise<void> {
    saving.value = true
    error.value = null
    try {
        await api.clearTaskInputValues(props.task.id)
        editing.value = false
        emit('changed')
    } catch (caught) {
        error.value = failure('clear', caught)
    } finally {
        saving.value = false
    }
}

watch(
    () => props.task.id,
    () => {
        editing.value = false
        error.value = null
    },
)
</script>

<template>
	<section class="space-y-3" data-testid="task-input-values">
		<div class="flex flex-wrap items-start justify-between gap-2">
			<div>
				<ZhSectionHeading>Input values</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">
					This enterprise's own value for each input the task declares. Every run takes it, however the run starts, unless the start gives its own; where neither does, the version's default applies. Saving changes no version, so you can set them on a task whose definition another enterprise owns.
				</p>
			</div>
			<Button
				v-if="editable && !editing && settable.length"
				type="button"
				size="sm"
				variant="outline"
				data-testid="input-values-edit"
				@click="startEditing"
			>
				Edit input values
			</Button>
		</div>
        <ZhPanel v-if="missingSetup.length" class="space-y-2 text-sm" data-testid="task-needs-setup">
            <ZhStatusPill tone="action" :pulse="false">Needs setup</ZhStatusPill>
            <p>Configure {{ missingSetup.map(field => field.label).join(', ') }} before running this task. For inputs in a required group, fill one of the alternatives.</p>
            <Button v-if="editable && !editing" size="sm" variant="outline" @click="startEditing">Configure task</Button>
        </ZhPanel>
		<p v-if="error" class="text-destructive text-sm" data-testid="input-values-error">{{ error }}</p>

		<ZhPanel v-if="ignored.length" as="p" class="flex items-start gap-2 text-sm" data-testid="input-values-ignored">
			<!-- The kit's action-required pill, glyph only: the sentence beside it says what needs doing. -->
			<ZhStatusPill tone="action" :pulse="false" class="shrink-0" aria-hidden="true" />
			<span>
				Saved values no run takes, because this version no longer declares the input or takes a value of that kind:
				<ZhSystemLabel v-for="key in ignored" :key="key" as="span" class="ml-1">{{ key }}</ZhSystemLabel>.
				Saving the input values again drops them.
			</span>
		</ZhPanel>

		<ZhPanel v-if="!editing" as="article" class="space-y-2 text-sm" data-testid="input-values-list">
			<div v-for="field in inputFields" :key="field.key" class="flex flex-wrap items-baseline gap-x-3 gap-y-1" :data-testid="`input-value-${field.key}`">
				<span class="font-medium">{{ field.label }}</span>
				<ZhSystemLabel as="span">{{ field.key }} · {{ field.type }}</ZhSystemLabel>
				<ZhStatusPill v-if="field.configuration" tone="neutral" :pulse="false">Configuration</ZhStatusPill>
                <span v-if="field.requiredGroup" class="text-muted-foreground">One of: {{ inputFields.filter(other => other.requiredGroup === field.requiredGroup).map(other => other.label).join(' / ') }}</span>
				<ZhStatusPill v-if="field.required" tone="neutral" :pulse="false">required</ZhStatusPill>
				<span class="text-muted-foreground">Version default: {{ shown(field.default) || 'none' }}</span>
				<span v-if="field.type === 'file'" class="text-muted-foreground">Given at each start</span>
				<span v-else :data-testid="`input-value-own-${field.key}`">Ours: {{ ownValue(field) || 'not set' }}</span>
			</div>
		</ZhPanel>

		<ZhPanel v-else as="form" class="space-y-3" data-testid="input-values-form" @submit.prevent="save">
			<div class="grid gap-3 sm:grid-cols-2">
				<div v-for="field in settable" :key="field.key" class="grid gap-1 text-sm">
					<span :id="`${fieldId}-${field.key}`">{{ field.label }}</span>
					<Select v-if="field.type === 'boolean'" v-model="draft[field.key]" :data-testid="`input-values-field-${field.key}`">
						<SelectTrigger class="w-full" :aria-labelledby="`${fieldId}-${field.key}`">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem :value="UNSET">Not set</SelectItem>
							<SelectItem value="true">true</SelectItem>
							<SelectItem value="false">false</SelectItem>
						</SelectContent>
					</Select>
					<Input
						v-else
						v-model="draft[field.key]"
						:aria-labelledby="`${fieldId}-${field.key}`"
						:data-testid="`input-values-field-${field.key}`"
						:inputmode="field.type === 'number' ? 'decimal' : undefined"
						:type="field.type === 'link' ? 'url' : 'text'"
						:placeholder="shown(field.default) ? `Default: ${shown(field.default)}` : 'Not set'"
					/>
				</div>
			</div>
			<p v-if="problem" class="text-destructive text-sm" data-testid="input-values-problem">{{ problem }}</p>
			<p v-else class="text-muted-foreground text-sm">Leave a field empty to use what each start gives, else the version's default.</p>
			<div class="flex flex-wrap gap-2">
				<Button type="submit" size="sm" data-testid="input-values-save" :disabled="saving || Boolean(problem)">
					{{ saving ? 'Saving…' : 'Save input values' }}
				</Button>
				<Button type="button" size="sm" variant="outline" :disabled="saving" @click="editing = false">Cancel</Button>
				<Button
					v-if="Object.keys(values).length"
					type="button"
					size="sm"
					variant="destructive"
					data-testid="input-values-clear"
					:disabled="saving"
					@click="clear"
				>
					Clear all
				</Button>
			</div>
		</ZhPanel>
	</section>
</template>
